import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { resolveTenantSlug, cacheTenantSlug, clearTenantSlug } from '../utils/tenantResolver';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEME_COLORS = {
  'theme-modern-organic': '#2E3B32',
  'theme-country': '#00B37A',
  'theme-zepto': '#5B2EBF',
  'theme-instamart': '#0C831F',
  'theme-blinkit': '#F7C948',
};

// Applying the Authorization header has to happen synchronously wherever the token
// changes — not only in the useEffect below. React flushes effects child-before-parent,
// so a page mounted by the same commit as a login (AdminProducts, AdminStock) fires its
// own data-loading effect BEFORE this provider's effect would set the header, and those
// first requests go out unauthenticated — which FastAPI's HTTPBearer answers with
// 403 "Not authenticated" (not 401), making it look like a permissions problem.
const applyAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Seed from storage at module load so the first request of a restored session is authed.
applyAuthToken(localStorage.getItem('token'));

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [storeSettings, setStoreSettings] = useState(null);
  const [tenantSlug, setTenantSlug] = useState(() => resolveTenantSlug());
  const [availableRoles, setAvailableRoles] = useState([]);

  // Safety net — the header is already applied synchronously at each token change
  // (see applyAuthToken above); this keeps it in sync for any other path that
  // updates token state.
  useEffect(() => {
    applyAuthToken(token);
  }, [token]);

  useEffect(() => {
    const slug = resolveTenantSlug();
    if (slug) {
      axios.defaults.headers.common['X-Tenant-Slug'] = slug;
      setTenantSlug(slug);
    }
  }, []);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          let userData = response.data;
          
          // Auto-register customer with the resolved tenant if admin_id is missing
          // This handles existing users from before tenant-aware login was implemented
          if (userData.role === 'customer' && !userData.admin_id) {
            const slug = resolveTenantSlug();
            if (slug) {
              try {
                const tenantRes = await axios.get(`${API}/store/by-slug/${slug}`);
                const tenantAdminId = tenantRes.data?.admin_id;
                if (tenantAdminId) {
                  await axios.post(`${API}/customer/register`, null, { params: { admin_id: tenantAdminId } });
                  const refreshed = await axios.get(`${API}/auth/me`);
                  userData = refreshed.data;
                }
              } catch { /* registration failed — will fall through to select-store */ }
            }
          }

          setUser(userData);
          
          // Load store settings if user has admin_id
          if (userData.admin_id) {
            const settingsRes = await axios.get(`${API}/store/${userData.admin_id}/settings`);
            setStoreSettings(settingsRes.data);
          } else if (userData.role === 'admin') {
            const settingsRes = await axios.get(`${API}/admin/settings`);
            setStoreSettings(settingsRes.data);
          } else {
            // Customer still without admin_id OR other roles — try loading settings from slug
            const slug = resolveTenantSlug();
            if (slug) {
              try {
                const settingsRes = await axios.get(`${API}/store/by-slug/${slug}`);
                setStoreSettings(settingsRes.data);
              } catch { /* not found */ }
            }
          }
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      } else {
        // Guest mode — load store settings from slug for product browsing
        const slug = resolveTenantSlug();
        if (slug) {
          try {
            const settingsRes = await axios.get(`${API}/store/by-slug/${slug}`);
            setStoreSettings(settingsRes.data);
          } catch { /* guest mode — store not found is ok */ }
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Normalize phone: strip country code (+91/91/0 prefix) to 10-digit Indian number
  // Mirrors backend's _normalize_phone — prevents mobile autofill issues
  const normalizePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
    if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
    return digits;
  };

  const sendOtp = async (phone, method = 'sms') => {
    const response = await axios.post(`${API}/auth/send-otp`, { phone: normalizePhone(phone), method });
    return response.data;
  };

  // Fetch available roles for the current user (for role switching)
  const fetchAvailableRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/available-roles`);
      setAvailableRoles(res.data.available_roles || []);
    } catch {
      setAvailableRoles([]);
    }
  }, []);

  // Fetch roles when user changes
  useEffect(() => {
    if (user && token) {
      fetchAvailableRoles();
    } else {
      setAvailableRoles([]);
    }
  }, [user?.id, token, fetchAvailableRoles]);

  const switchRole = async (targetRole) => {
    const response = await axios.post(`${API}/auth/switch-role`, { role: targetRole });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('auth_token', access_token);
    applyAuthToken(access_token);
    setToken(access_token);
    setUser(userData);
    // Reload store settings for the new role
    if (userData.admin_id) {
      const settingsRes = await axios.get(`${API}/store/${userData.admin_id}/settings`);
      setStoreSettings(settingsRes.data);
    } else if (userData.role === 'admin') {
      try {
        const settingsRes = await axios.get(`${API}/admin/settings`);
        setStoreSettings(settingsRes.data);
      } catch { /* Settings might not exist yet */ }
    }
    return userData;
  };

  const verifyOtp = async (phone, otp, role = 'customer') => {
    const cleanOtp = otp.replace(/\D/g, '');
    const response = await axios.post(`${API}/auth/verify-otp`, { phone: normalizePhone(phone), otp: cleanOtp, role });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('auth_token', access_token); // For pushService token sync
    applyAuthToken(access_token);
    setToken(access_token);
    setUser(userData);

    // Load store settings
    if (userData.admin_id) {
      const settingsRes = await axios.get(`${API}/store/${userData.admin_id}/settings`);
      setStoreSettings(settingsRes.data);
    } else if (userData.role === 'admin') {
      try {
        const settingsRes = await axios.get(`${API}/admin/settings`);
        setStoreSettings(settingsRes.data);
      } catch (e) {
        // Settings might not exist yet for new admin
      }
    }
    
    return userData;
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/auth/profile`, null, { params: data });
    setUser(response.data);
    return response.data;
  };

  const registerWithStore = async (adminId) => {
    await axios.post(`${API}/customer/register`, null, { params: { admin_id: adminId } });
    
    // Reload user to get updated admin_id
    const response = await axios.get(`${API}/auth/me`);
    setUser(response.data);
    
    // Load store settings
    const settingsRes = await axios.get(`${API}/store/${adminId}/settings`);
    setStoreSettings(settingsRes.data);
    
    return response.data;
  };

  const logout = () => {
    // Capture tokens BEFORE clearing localStorage (race condition fix)
    const authTok = localStorage.getItem('auth_token') || localStorage.getItem('token');
    import('../utils/pushService').then(({ unregisterToken }) => unregisterToken(authTok)).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('impersonating');
    setToken(null);
    setUser(null);
    setStoreSettings(null);
    applyAuthToken(null);
  };

  const refreshStoreSettings = useCallback(async () => {
    try {
      if (user?.admin_id) {
        const settingsRes = await axios.get(`${API}/store/${user.admin_id}/settings`);
        setStoreSettings(settingsRes.data);
      } else if (user?.role === 'admin') {
        const settingsRes = await axios.get(`${API}/admin/settings`);
        setStoreSettings(settingsRes.data);
      }
    } catch (error) {
      console.error('Failed to refresh store settings:', error);
    }
  }, [user?.admin_id, user?.role]);

  // Periodic refresh of store settings to pick up theme/config changes
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(refreshStoreSettings, 30000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshStoreSettings();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, refreshStoreSettings]);

  // Dynamic PWA meta tags per tenant (manifest is handled server-side via Host header)
  useEffect(() => {
    if (!storeSettings) return;

    // Update theme-color meta
    const themeColor = THEME_COLORS[storeSettings.theme] || '#2E3B32';
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.content = themeColor;

    // Update page title & description
    if (storeSettings.store_name) {
      document.title = storeSettings.store_name;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.content = `Order fresh groceries & milk from ${storeSettings.store_name}`;
      const appleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (appleMeta) appleMeta.content = storeSettings.store_name;
    }

    // Update apple-touch-icon if store has a logo
    if (storeSettings.store_logo) {
      const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (appleIcon) appleIcon.href = storeSettings.store_logo;
    }
  }, [storeSettings]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        storeSettings,
        tenantSlug,
        availableRoles,
        sendOtp,
        verifyOtp,
        updateProfile,
        registerWithStore,
        switchRole,
        logout,
        refreshStoreSettings,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isSuperAdmin: user?.role === 'super_admin',
        isCustomer: user?.role === 'customer',
        isDeliveryBoy: user?.role === 'delivery_boy',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
